/*
 * The cart shared with ski.sease.it (the headless POV site): sease.it's half. Its other half and the plan are in the
 * sease-headless repo (lib/shopify/shared-cart.ts, docs/tracking/PLAN.md §4). Loaded on every page by layout/theme.liquid.
 *
 * Both sites sell from the same store, and a theme cart IS a Storefront API cart: the theme's `cart` cookie holds
 * `<token>?key=<key>`, and ski.sease.it addresses the same cart as `gid://shopify/Cart/<token>?key=<key>`. The two
 * sites agree on which cart is current through one pointer cookie on the parent domain:
 *
 *   sease_cart = encodeURIComponent("<token>?key=<key>")   Domain=.sease.it, 90 days
 *
 * On load:  same cart as the pointer → nothing to do.
 *           pointer's cart is gone (checked out / expired) → point at ours, or clear.
 *           ours empty → adopt the pointer's cart (rewrite our `cart` cookie).
 *           both have items → move our lines into the pointer's cart, then adopt it.
 *           adopted a cart priced in another market → switch it to this market.
 * After any AJAX cart change → point the pointer at our current cart.
 *
 * When the cart changed under the page it fires `sease:cart-synced` (detail: the cart). A listener that refreshes the
 * cart bubble/drawer itself should call event.preventDefault(); otherwise the page reloads once to show the right cart.
 * Tested against the live store 2026-11-04: ski → sease.it adoption (also from a fresh session, which needs bind() and
 * hold(): the page's own cart writes race the switch), a sease.it add landing in the shared cart, and ski.sease.it
 * adopting it back.
 */
(function () {
  "use strict";
  var POINTER = "sease_cart";
  var DOMAIN = ".sease.it";
  var MAX_AGE = 60 * 60 * 24 * 90;
  if (window.__seaseSharedCart) return;
  window.__seaseSharedCart = true;

  function readCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch (e) { return null; }
  }
  function setPointer(token) {
    document.cookie = POINTER + "=" + (token ? encodeURIComponent(token) : "") +
      "; path=/; domain=" + DOMAIN + "; max-age=" + (token ? MAX_AGE : 0) + "; samesite=lax; secure";
  }
  // The theme's own cart cookie: host-only, raw value, as Shopify sets it; null removes it.
  function setThemeCart(token) {
    document.cookie = "cart=" + (token || "") + "; path=/; max-age=" + (token ? MAX_AGE : 0) + "; samesite=lax";
  }
  function getCart() {
    return fetch("/cart.js", { credentials: "same-origin", cache: "no-store" }).then(function (r) { return r.json(); });
  }
  // Shopify keeps the cart it serves in a session cookie scripts can't see, not only in `cart`: a cart only adopted
  // through `cart` is served for the next request, then dropped for a new one on the next page. Any write to the
  // adopted cart makes it the session's (2026-11-04, a fresh session from ski.sease.it landed on an empty cart).
  function bind() {
    return fetch("/cart/update.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: {} }),
    });
  }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  // The cart `token` names, presented as ours; asked again once before it counts as gone, since a late answer to another
  // cart request can put a different cart in its place for a moment.
  function look(token, tries) {
    setThemeCart(token);
    return getCart().then(function (cart) {
      return cart.token === token || tries <= 0 ? cart : wait(800).then(function () { return look(token, tries - 1); });
    });
  }
  // Shopify's session and our cookie both on `token`, re-claimed until they hold (a few times at most): a cart request
  // the page sent before the switch (wetracked writes its attributes on every page) can still answer with its own
  // cart and take the session back.
  function hold(token, tries) {
    return wait(800).then(getCart).then(function (cart) {
      if ((cart.token === token && readCookie("cart") === token) || tries <= 0) return cart;
      setThemeCart(token);
      return bind().then(function () { return hold(token, tries - 1); });
    });
  }
  function addLines(items) {
    return fetch("/cart/add.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: items }),
    });
  }
  // A cart keeps the currency it was made in; move it to the market this page is in.
  function alignMarket(cart) {
    var active = window.Shopify && Shopify.currency && Shopify.currency.active;
    var country = window.Shopify && Shopify.country;
    if (!active || !country || cart.currency === active) return Promise.resolve(cart);
    var f = new FormData();
    f.append("form_type", "localization");
    f.append("utf8", "✓");
    f.append("_method", "put");
    f.append("country_code", country);
    f.append("return_to", "/cart.js");
    return fetch("/localization", { method: "POST", body: f, credentials: "same-origin" })
      .then(getCart).catch(function () { return cart; });
  }
  function synced(cart) {
    var ev = new CustomEvent("sease:cart-synced", { detail: cart, cancelable: true });
    if (window.dispatchEvent(ev)) location.reload();
  }

  // One at a time: two merges at once would add the same lines twice.
  var running = null;
  function sync() {
    if (!running) running = run().then(function () { running = null; }, function () { running = null; });
    return running;
  }
  function run() {
    return getCart().then(function (ours) {
      var shared = readCookie(POINTER);
      if (!shared) { if (ours.item_count > 0) setPointer(ours.token); return; }
      if (shared === ours.token) return;

      // Look at the pointer's cart by presenting its token as our cart. Without a `cart` cookie of our own (a first
      // visit) /cart.js answers with a placeholder token, so what goes back is the cookie as it was, or none.
      var mine = readCookie("cart");
      return look(shared, 1).then(function (theirs) {
        if (theirs.token !== shared) {
          // Gone: Shopify handed back a fresh cart. Keep ours.
          setThemeCart(mine);
          setPointer(ours.item_count > 0 ? ours.token : null);
          return;
        }
        if (theirs.item_count === 0) {
          // Nothing to take: keep ours and point at it.
          setThemeCart(mine);
          setPointer(ours.item_count > 0 ? ours.token : null);
          return;
        }
        var moved = ours.item_count === 0 ? bind() : addLines(ours.items.map(function (i) {
          return { id: i.variant_id, quantity: i.quantity, properties: i.properties || {} };
        }));
        return moved.then(function () { return hold(shared, 3); }).then(alignMarket).then(function (cart) {
          // Not held: leave the pointer on the shared cart for the next page to try again.
          if (cart.token !== shared) return;
          setPointer(cart.token);
          synced(cart);
        });
      });
    }).catch(function () { /* leave the page's cart as it is */ });
  }

  // After any AJAX cart change, point the shared cookie at our cart when it is a new one with something in it: a page's
  // own write on a first visit makes an empty cart, which must not take the place of the one ski.sease.it is holding.
  var CHANGE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function afterChange() {
    var token = readCookie("cart");
    if (!token || token === readCookie(POINTER)) return;
    getCart().then(function (cart) {
      if (cart.token === token && cart.item_count > 0) setPointer(token);
    }).catch(function () {});
  }
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var p = origFetch.apply(this, arguments);
    if (CHANGE.test(url)) p.then(afterChange, function () {});
    return p;
  };
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CHANGE.test(String(url))) this.addEventListener("load", afterChange);
    return origOpen.apply(this, arguments);
  };
  // Back from ski.sease.it in another tab: re-check.
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible" && readCookie(POINTER) !== readCookie("cart")) sync();
  });

  // Once the page's own cart requests are done (hold() catches a late one).
  function start() { setTimeout(sync, 1000); }
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start);
})();
